import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FC } from 'react';
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_CANCEL, LABEL_DELETE_CONTACT } from '../../../../shared/lib/constants';
import { MSG_CONTACT_DELETED_SUCCESSFULLY } from '../../app/constants';
import palette from '../../../../shared/lib/theme/palette';
import { useAppContext } from '../../app/contexts/AppContext';
import { DeleteContactsProps } from '../../app/interfaceTypes';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { useDeleteContactMutation } from '../../generated';
import { CustomModal } from '../common/CustomModal';

export const DeleteContact: FC<DeleteContactsProps> = ({ currentCardData, refetch }) => {
  const { isDelete, dispatch, cardsData, selectedPlan, contactCount } = useAppContext();
  const { name, id: currentId, priority, phoneNumber } = currentCardData ?? {};
  const { metadata } = selectedPlan ?? {};
  const { contacts } = metadata || {};
  const updatedContactsValue = parseInt(contacts);
  const { blackTwo, borderLight, blackOne } = palette;
  const cardsValueUpdated = isNaN(updatedContactsValue) ? contactCount : updatedContactsValue;

  const handleClose = () => {
    dispatch({ type: ActionType.SET_IS_DELETE, isDelete: false });
  };

  const [deleteContact, { loading }] = useDeleteContactMutation({
    onError({ message }) {
      handleClose();
      return Alert.error(message);
    },

    onCompleted(data) {
      const { deleteContact } = data || {};
      if (deleteContact) {
        const updatedContacts = cardsData?.filter((contact) => contact.id !== currentId);

        dispatch({ type: ActionType.SET_CARDS_DATA, cardsData: updatedContacts });
        updatedContacts.length === 1 && dispatch({ type: ActionType.SET_DRAG_AND_DROP, dragAndDrop: false });
        handleClose();
        refetch();
        return Alert.success(MSG_CONTACT_DELETED_SUCCESSFULLY);
      }
    }
  });

  const removeContact = async () => {
    if (currentId) {
      await deleteContact({
        variables: {
          deleteContactInput: {
            id: currentId,
            contactLimit: cardsValueUpdated,
            priority: priority!
          }
        }
      });
    }
  };

  return (
    <CustomModal maxWidth="500px" isOpen={isDelete} handleClose={handleClose}>
      <Box padding="0 8px">
        <Box position="relative">
          <Box padding="24px 0">
            <Box display="flex" alignItems="center" mb="5px">
              <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />

              <Typography variant="h6" sx={{ fontWeight: '700', color: 'blackOne', marginTop: '4px' }}>
                Delete contact
              </Typography>
            </Box>

            <Typography variant="h2" color={blackTwo}>
              Are you sure you want to remove this contact from your list?
            </Typography>
          </Box>

          <Box my={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: '700', mb: 1 }}>
              {name}
            </Typography>
            <Typography variant="subtitle2">{phoneNumber}</Typography>
          </Box>

          <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
            <Button fullWidth onClick={handleClose} variant="text" sx={{ border: `1px solid ${borderLight}`, color: blackOne }}>
              {BUTTON_TEXT_CANCEL}
            </Button>
            <Button fullWidth onClick={removeContact} variant="contained" color="error" disabled={loading} endIcon={loading && <CircularProgress size={20} color="inherit" />}>
              {LABEL_DELETE_CONTACT}
            </Button>
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};
